import React from 'react'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import TuneIcon from '@mui/icons-material/Tune'
import { SxProps, Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import NoRowsOverlay from './NoRowsOverlay'
import GridLoadingOverlay from './GridLoadingOverlay'

export const listPageGridSlots = {
  noRowsOverlay: NoRowsOverlay,
  loadingOverlay: GridLoadingOverlay,
}

export const LIST_PAGE_COLORS = {
  ink: '#061524',
  text: '#0B1E2D',
  muted: '#5F7484',
  muted2: '#8A9BA7',
  line: '#DFE7E8',
  lineSoft: '#EAF0F0',
  surface: '#FFFFFF',
  surface2: '#F7FAFA',
  teal: '#0D9B87',
  tealDeep: '#076B5C',
  tealSoft: '#E1F4F0',
}

export const listPageChipSx = (on: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 32,
  padding: '0 12px',
  borderRadius: 99,
  border: on
    ? '1px solid transparent'
    : `1px solid ${LIST_PAGE_COLORS.line}`,
  background: on ? LIST_PAGE_COLORS.tealSoft : LIST_PAGE_COLORS.surface,
  fontSize: 15,
  fontFamily: 'Lato, sans-serif',
  fontWeight: on ? 500 : 400,
  color: on ? LIST_PAGE_COLORS.tealDeep : LIST_PAGE_COLORS.muted,
  cursor: 'pointer',
  transition: 'all 0.14s',
})

export const listPageIconBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  display: 'grid',
  placeItems: 'center',
  color: LIST_PAGE_COLORS.muted,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
}

export const listPageRowActionsStyle: React.CSSProperties = {
  display: 'flex',
  width: '100%',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 10,
  opacity: 0.55,
  transition: 'opacity 0.14s',
}

export const LIST_PAGE_TABLE_HEIGHT = 520

export const listPageDataGridSx = (rowCount: number): SxProps<Theme> => {
  const empty = rowCount === 0
  const contentHeight = 42 + Math.max(rowCount, 1) * 56
  const height = empty
    ? 180
    : Math.min(LIST_PAGE_TABLE_HEIGHT, contentHeight)

  return {
  border: 'none',
  borderRadius: 0,
  fontFamily: 'Lato, sans-serif',
  height,
  '& .MuiDataGrid-main': {
    minHeight: empty ? 160 : 'unset',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: `${LIST_PAGE_COLORS.surface2} !important`,
    borderBottom: `1px solid ${LIST_PAGE_COLORS.line} !important`,
    minHeight: '42px !important',
    maxHeight: '42px !important',
    borderRadius: 0,
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: `${LIST_PAGE_COLORS.surface2} !important`,
    padding: '0 16px !important',
    outline: 'none !important',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
    {
      outline: 'none !important',
    },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontFamily: 'Lato, sans-serif !important',
    fontSize: '13.5px !important',
    fontWeight: '600 !important',
    letterSpacing: '0.12em !important',
    textTransform: 'uppercase !important',
    color: `${LIST_PAGE_COLORS.muted2} !important`,
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    justifyContent: 'flex-start !important',
  },
  '& .MuiDataGrid-columnHeader[data-field="Action"] .MuiDataGrid-columnHeaderTitleContainer':
    {
      justifyContent: 'flex-end !important',
    },
  '& .MuiDataGrid-row': {
    minHeight: '56px !important',
    maxHeight: '56px !important',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: `${LIST_PAGE_COLORS.surface2} !important`,
      '& .list-row-actions': { opacity: '1 !important' },
    },
    '&:last-child .MuiDataGrid-cell': {
      borderBottom: 'none',
    },
  },
  '& .incyia-name-link': {
    color: '#00146d',
    textDecoration: 'none',
    cursor: 'pointer',
    display: 'inline',
    maxWidth: '100%',
    transition: 'color 0.14s',
    '&:hover': {
      color: '#076b5c',
    },
    '&:hover .incyia-desc, &:hover .incyia-desc--primary': {
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
      textDecorationThickness: '1px',
    },
  },
  '& .MuiDataGrid-row:hover .incyia-name-link': {
    color: '#076b5c',
  },
  '& .MuiDataGrid-row:hover .incyia-name-link .incyia-desc, & .MuiDataGrid-row:hover .incyia-name-link .incyia-desc--primary':
    {
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
      textDecorationThickness: '1px',
    },
  '& .incyia-name-link .incyia-desc, & .incyia-name-link .incyia-desc--primary':
    {
      cursor: 'pointer',
      color: 'inherit',
      textDecoration: 'none',
    },
  '& .MuiDataGrid-cell[data-field="PipelineName"], & .MuiDataGrid-cell[data-field="MissionName"], & .MuiDataGrid-cell[data-field="AuditName"], & .MuiDataGrid-cell[data-field="ProjetName"], & .MuiDataGrid-cell[data-field="ActionName"], & .MuiDataGrid-cell[data-field="PlanName"], & .MuiDataGrid-cell[data-field="RisqueName"], & .MuiDataGrid-cell[data-field="IncidentName"]':
    {
      cursor: 'pointer',
    },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '56px !important',
    maxHeight: '56px !important',
    borderBottom: `1px solid ${LIST_PAGE_COLORS.lineSoft}`,
    padding: '14px 16px !important',
    fontSize: 16,
    outline: 'none !important',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none !important',
  },
  '& .MuiDataGrid-cell[data-field="Action"]': {
    justifyContent: 'flex-end !important',
  },
  '& .MuiDataGrid-virtualScroller': {
    background: LIST_PAGE_COLORS.surface,
  },
  '& .MuiDataGrid-overlayWrapper': {
    minHeight: empty ? 140 : 0,
  },
  '& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller': {
    display: 'none',
  },
  '& .MuiDataGrid-scrollbar': {
    '&.MuiDataGrid-scrollbar--vertical': {
      width: 8,
    },
    '&.MuiDataGrid-scrollbar--horizontal': {
      height: 8,
    },
  },
}
}

type ListPageShellProps = {
  eyebrow: string
  title: string
  titleTourId?: string
  subtitle: string
  addLabel?: string
  onAdd?: () => void
  /** Extra actions in the page head (e.g. View Analysis) */
  headActions?: React.ReactNode
  search: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  countLabel: string
  chips?: React.ReactNode
  showFiltersChip?: boolean
  footer?: React.ReactNode
  children: React.ReactNode
}

export function ListPageShell({
  eyebrow,
  title,
  titleTourId,
  subtitle,
  addLabel,
  onAdd,
  headActions,
  search,
  onSearchChange,
  searchPlaceholder = 'Filter by name',
  countLabel,
  chips,
  showFiltersChip = true,
  footer,
  children,
}: ListPageShellProps) {
  const { t } = useTranslation()
  const C = LIST_PAGE_COLORS

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 20,
          marginBottom: 20,
          flexWrap: 'wrap',
          fontFamily: 'Lato, sans-serif',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: '#5F7484',
              marginBottom: 7,
            }}
          >
            {eyebrow}
          </div>
          <h1
            data-tour-id={titleTourId}
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 700,
              color: C.text,
              letterSpacing: '-0.008em',
              lineHeight: 1.12,
            }}
          >
            {title}
          </h1>
          <p
            style={{
              margin: '8px 0 0',
              color: C.muted,
              fontSize: 16.5,
              maxWidth: '72ch',
              lineHeight: 1.55,
            }}
          >
            {subtitle}
          </p>
        </div>

        {(headActions || (addLabel && onAdd)) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            {headActions}
            {addLabel && onAdd && (
              <button
                type="button"
                onClick={onAdd}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  height: 38,
                  padding: '0 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: C.teal,
                  color: '#fff',
                  fontSize: 14.5,
                  fontWeight: 500,
                  fontFamily: 'Lato, sans-serif',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(13,155,135,0.35)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = C.tealDeep
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = C.teal
                }}
              >
                <AddIcon style={{ fontSize: 16 }} />
                {addLabel}
              </button>
            )}
          </div>
        )}
      </div>

      <div
        style={{
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 10,
          boxShadow:
            '0 1px 2px rgba(6,21,36,.05), 0 10px 28px -20px rgba(6,21,36,.35)',
          overflow: 'hidden',
          fontFamily: 'Lato, sans-serif',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderBottom: `1px solid ${C.lineSoft}`,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 36,
              padding: '0 12px',
              background: C.surface2,
              border: `1px solid ${C.line}`,
              borderRadius: 8,
              minWidth: 220,
              flex: 1,
              maxWidth: 320,
            }}
          >
            <SearchIcon
              sx={{ fontSize: 17, color: C.muted2, flexShrink: 0 }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                border: 'none',
                background: 'none',
                outline: 'none',
                width: '100%',
                fontSize: 15.5,
                fontFamily: 'Lato, sans-serif',
                color: C.text,
              }}
            />
          </div>

          {showFiltersChip && (
            <button type="button" style={listPageChipSx(false)}>
              <TuneIcon sx={{ fontSize: 15 }} />
              {t('listPages.filters')}
            </button>
          )}

          {chips}

          <span
            style={{
              marginLeft: 'auto',
              fontSize: 16,
              color: C.muted,
              whiteSpace: 'nowrap',
            }}
          >
            {countLabel}
          </span>
        </div>

        {children}

        {footer && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 16px',
              borderTop: `1px solid ${C.lineSoft}`,
              flexWrap: 'wrap',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </>
  )
}
